import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useQueryWithErrorHandler } from './useQueryWithErrorHandler';
import { refetchQueries } from './refetchQueries';
import {
  IApiParams,
  IChangeProbeValidity,
  ICreateManualProbe,
  IError,
  IProbeRanges,
  IResponseData,
} from 'types';
import { useAuth, useError } from '../providers';
import { useAuthClient } from './useAuthClient';
import { firebaseAnalytics } from '../analytics';
import { errors } from '../config';

const manualProbeRangesQueryKey = 'getProbeRanges';
const manualProbeDataQueryKey = 'manualProbeQueryKey';
const invalidateProbeQueryKey = 'invalidateProbe';

export function useManualProbeApi({
  organizationId,
  queryParameters,
}: IApiParams) {
  const authClient = useAuthClient();
  const { openModal } = useError();
  const { supplierId } = useAuth();
  const queryClient = useQueryClient();

  const currentSupplierId = useMemo(() => {
    return organizationId || supplierId;
  }, [organizationId, supplierId]);

  const getProbeRangesUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/probes/get-ranges`;
  }, [currentSupplierId]);

  const createManualProbeUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/probes/create-manual-probe`;
  }, [currentSupplierId]);

  const invalidateManualProbeUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/probes/change-validity`;
  }, [currentSupplierId]);

  const {
    data: probeRangesResponse,
    refetch: fetchProbeRanges,
    isLoading: isFetchingProbeRanges,
    error: fetchProbeRangesError,
  } = useQueryWithErrorHandler<IResponseData<IProbeRanges>, IError>({
    queryKey: [manualProbeRangesQueryKey],

    queryFn: async () => {
      return await authClient(`${getProbeRangesUrl}`, {
        method: 'GET',
        queryParameters,
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,

    onError: error => {
      firebaseAnalytics.logErrorEvent(errors.getProbeRanges, error, {
        getProbeRangesUrl: getProbeRangesUrl,
      });
      openModal(error);
    },
  });

  const {
    mutateAsync: createManualProbe,
    isPending: isCreateManualProbe,
    error: createManualProbeError,
  } = useMutation({
    mutationFn: (payload: ICreateManualProbe) =>
      authClient(createManualProbeUrl, {
        method: 'POST',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [manualProbeDataQueryKey],
      });
      return queryClient.getQueryData<IResponseData<boolean>>([
        manualProbeDataQueryKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<boolean>>(
          [manualProbeDataQueryKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(errors.createManualProbe, error, {
        createManualProbeUrl,
        ...variables,
      });
      openModal(error);
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [manualProbeDataQueryKey],
      });
      await refetchQueries(queryClient, {
        queryKey: [manualProbeDataQueryKey],
      });
    },
  });

  const {
    mutateAsync: invalidateManualProbe,
    isPending: isInvalidateManualProbe,
    error: invalidateManualProbeError,
  } = useMutation({
    mutationFn: (payload: IChangeProbeValidity) =>
      authClient(invalidateManualProbeUrl, {
        method: 'PATCH',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [invalidateProbeQueryKey],
      });
      return queryClient.getQueryData<IResponseData<boolean>>([
        invalidateProbeQueryKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<boolean>>(
          [invalidateProbeQueryKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(errors.invalidateManualProbe, error, {
        invalidateManualProbeUrl,
        ...variables,
      });
      openModal(error);
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [manualProbeDataQueryKey],
      });
      await refetchQueries(queryClient, {
        queryKey: [manualProbeDataQueryKey],
      });
    },
  });

  const probeRanges = useMemo<IProbeRanges | undefined>(
    () => probeRangesResponse?.data,
    [probeRangesResponse],
  );

  return {
    probeRanges,
    fetchProbeRanges,
    isFetchingProbeRanges,
    fetchProbeRangesError,
    createManualProbe,
    isCreateManualProbe,
    createManualProbeError,
    invalidateManualProbe,
    isInvalidateManualProbe,
    invalidateManualProbeError,
  };
}
